import { useMemo, useState } from 'react'
import { Link2, Plus, RotateCcw } from 'lucide-react'
import { toast } from 'sonner'
import { apiPut } from '@/api/axios-client'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { SearchSelect } from '@/components/composites/search-select'
import { ClientDialog, type ClientDraftPayload } from '@/features/clients/ClientDialog'
import { localTimeZone } from '@/lib/timezone'
import type { Client } from '@/types/entities'

export const UNMAPPED_REQUESTER_FILTER = '__unmapped__'

export type RequesterMatchKind =
  | 'manual'
  | 'exact_name'
  | 'legal_name'
  | 'email_domain'
  | 'ambiguous'
  | 'unmatched'

export interface SupportRequesterResolution {
  key: string
  rawLabel: string
  displayLabel: string
  clientId: string | null
  clientName: string | null
  match: RequesterMatchKind
  ambiguousClientIds: string[]
}

export interface RequesterClientOption {
  id: string
  name: string
  archived: boolean
  accountOwnerId: string
  allowedDomains: string[]
}

const MATCH_LABEL: Record<RequesterMatchKind, string> = {
  manual: 'Manual link',
  exact_name: 'Matched by Client name',
  legal_name: 'Matched after ignoring legal suffix',
  email_domain: 'Matched by Intercom email domain',
  ambiguous: 'Needs review',
  unmatched: 'Not linked',
}

function createClientDraft(name: string): ClientDraftPayload {
  return {
    name,
    stage: '',
    accountOwnerId: '',
    nextMeetingAt: '',
    meetingZone: localTimeZone(),
    contacts: [],
    geographies: [],
    allowedDomains: [],
    notes: '',
  }
}

export function requesterClientPickerOptions(clients: RequesterClientOption[]) {
  const counts = new Map<string, number>()
  for (const client of clients) {
    const key = client.name.trim().toLowerCase()
    counts.set(key, (counts.get(key) ?? 0) + 1)
  }
  return clients.map((client) => {
    const duplicate = (counts.get(client.name.trim().toLowerCase()) ?? 0) > 1
    const hints = [
      duplicate ? `Duplicate name · Client ID …${client.id.slice(-6)}` : null,
      client.archived ? 'Former client' : null,
    ].filter(Boolean)
    return {
      id: client.id,
      label: client.name,
      hint: hints.join(' · ') || undefined,
      group: client.archived ? 'Former clients' : 'Active clients',
    }
  })
}

export function RequesterMappingDialog({
  requester,
  clients,
  members,
  canMap,
  canCreateClient,
  onChanged,
}: {
  requester: SupportRequesterResolution
  clients: RequesterClientOption[]
  members: Array<{ id: string; displayName: string; photoUrl?: string | null }>
  canMap: boolean
  canCreateClient: boolean
  onChanged: () => void | Promise<void>
}) {
  const [open, setOpen] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [selectedClientId, setSelectedClientId] = useState(requester.clientId ?? '')
  const [busy, setBusy] = useState(false)
  const options = useMemo(() => requesterClientPickerOptions(clients), [clients])
  const selectedClient = clients.find((client) => client.id === selectedClientId) ?? null
  const currentClient = requester.clientId ? clients.find((client) => client.id === requester.clientId) ?? null : null
  const duplicateCurrentName = currentClient
    ? clients.filter((client) => client.name.trim().toLowerCase() === currentClient.name.trim().toLowerCase()).length > 1
    : false
  const createName = !requester.clientId && requester.rawLabel !== 'Unknown requester' ? requester.rawLabel : ''

  const saveMapping = async (clientId: string | null) => {
    if (!canMap || busy) return
    setBusy(true)
    try {
      await apiPut('support-tickets/requester-mapping', {
        requesterKey: requester.key,
        requesterLabel: requester.rawLabel,
        clientId,
      })
      toast.success(clientId ? `Requester linked to ${clients.find((client) => client.id === clientId)?.name ?? 'Client'}.` : 'Manual requester link cleared; automatic matching restored.')
      setOpen(false)
      await onChanged()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not update requester mapping.')
    } finally {
      setBusy(false)
    }
  }

  const beginCreate = () => {
    setOpen(false)
    setTimeout(() => setCreateOpen(true), 0)
  }

  const onClientCreated = async (client: Client) => {
    setCreateOpen(false)
    if (!canMap) {
      await onChanged()
      return
    }
    setBusy(true)
    try {
      await apiPut('support-tickets/requester-mapping', {
        requesterKey: requester.key,
        requesterLabel: requester.rawLabel,
        clientId: client.id,
      })
      toast.success(`Created ${client.name} and linked this requester.`)
      await onChanged()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Client was created, but the requester link could not be saved.')
      await onChanged()
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={!canMap}
        onClick={() => {
          setSelectedClientId(requester.clientId ?? '')
          setOpen(true)
        }}
      >
        <Link2 className="size-3.5" />
        {requester.clientId ? 'Change mapping' : 'Link requester'}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{requester.clientId ? 'Change requester mapping' : 'Link requester to Client'}</DialogTitle>
            <DialogDescription>
              The Support filter/display uses the canonical Tapwork Client name after linking. The original Intercom requester text is kept as source metadata.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-1">
            <div className="rounded-lg border border-border bg-muted/30 p-3 text-xs">
              <p className="font-semibold text-text-primary">Intercom requester</p>
              <p className="mt-1 text-text-secondary">{requester.rawLabel}</p>
              <p className="mt-1 text-text-muted">{MATCH_LABEL[requester.match]}{requester.clientName ? ` · ${requester.clientName}` : ''}</p>
            </div>

            <div>
              <Label className="mb-1.5 block text-tiny">Tapwork Client</Label>
              <SearchSelect
                value={selectedClientId}
                onChange={setSelectedClientId}
                options={options}
                placeholder="Search Clients…"
                emptyHint="No Clients exist yet."
                stackHints
              />
            </div>

            <p className="rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-text-secondary">
              Client names are not guaranteed to be unique. If two Clients share the same name, verify the intended record before linking. Tapwork saves this mapping to the specific Client ID, not merely the displayed name.
            </p>

            {requester.match === 'ambiguous' || duplicateCurrentName ? (
              <p className="text-xs font-medium text-warning-foreground">
                This requester has a duplicate/ambiguous Client-name match, so Tapwork will not guess which record you mean.
              </p>
            ) : null}
          </div>

          <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
            <div className="flex flex-wrap gap-2">
              {canCreateClient && createName ? (
                <Button type="button" variant="outline" onClick={beginCreate}>
                  <Plus className="size-3.5" /> Create “{createName}”
                </Button>
              ) : null}
              {requester.match === 'manual' ? (
                <Button type="button" variant="outline" disabled={busy} onClick={() => void saveMapping(null)}>
                  <RotateCcw className="size-3.5" /> Reset to automatic
                </Button>
              ) : null}
            </div>
            <Button type="button" disabled={!selectedClient || busy} onClick={() => void saveMapping(selectedClientId)}>
              {requester.clientId ? 'Save mapping' : 'Link Client'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {canCreateClient && createName ? (
        <ClientDialog
          key={requester.key}
          members={members}
          open={createOpen}
          onOpenChange={setCreateOpen}
          draftPayload={createClientDraft(createName)}
          onCreated={(client) => void onClientCreated(client)}
        />
      ) : null}
    </>
  )
}
